import React, { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import styles from '../../styles/test/TestStyles.module.css';

/**
 * TestQuestion component for displaying test questions and handling answers
 * Works in both practice and random test modes
 */
const TestQuestion = ({
  pregunta,
  opciones,
  letras,
  respuestaActual,
  respuestaCorrecta,
  onRespuesta,
  mostrarFeedback = false,
  disabled = false,
  onPrevious = null,
  onNext = null,
  currentQuestion = 0,
  totalQuestions = 0,
  hasNextQuestion = false,
  isPracticeMode = false
}) => {
  const canGoBack = isPracticeMode && currentQuestion > 0 && onPrevious;
  const canGoForward = isPracticeMode && hasNextQuestion && respuestaActual?.seleccionada !== undefined && onNext;

  // Memoize option classes calculation
  const getOptionClass = useCallback((index, isSelected, isCorrect, isRevealed) => {
    let optionClass = styles.optionButton;
    
    if (mostrarFeedback || isRevealed) {
      if (isSelected && isCorrect) {
        optionClass += ` ${styles.correct}`;
      } else if (isSelected && !isCorrect && !isRevealed) {
        optionClass += ` ${styles.incorrect}`;
      } else if (!isSelected && isCorrect) {
        optionClass += ` ${styles.correct}`;
      } else if (isRevealed) {
        optionClass += ` ${styles.revealed}`;
      } else {
        optionClass += ` ${styles.faded}`;
      }
    } else if (isSelected) {
      optionClass += ` ${styles.selected}`;
    }
    
    return optionClass;
  }, [mostrarFeedback]);

  // Memoize revealed state check
  const isAnswerRevealed = useMemo(() =>
    respuestaActual?.seleccionada === -1 && respuestaActual?.revelada,
    [respuestaActual]
  );

  // Memoize options rendering
  const renderedOptions = useMemo(() =>
    opciones.map((opcion, index) => {
      const isCorrect = index === respuestaCorrecta;
      const isSelected = respuestaActual?.seleccionada === index;
      const optionClass = getOptionClass(index, isSelected, isCorrect, isAnswerRevealed);
      
      const handleClick = () => {
        if (!disabled && !isAnswerRevealed) {
          onRespuesta(index);
        }
      };

      return (
        <button
          key={`question-${currentQuestion}-option-${index}`}
          onClick={handleClick}
          className={optionClass}
          disabled={disabled || isAnswerRevealed}
          aria-pressed={isSelected}
          aria-disabled={disabled || isAnswerRevealed}
          aria-label={`Opción ${letras[index]}: ${opcion}`}
          role="option"
          aria-selected={isSelected}
        >
          <span className={styles.optionLetter} aria-hidden="true">
            {letras[index]}
          </span>
          <span className={styles.optionText}>{opcion}</span>
          {isAnswerRevealed && isCorrect && (
            <span className={styles.revealedIndicator} aria-label="Respuesta correcta revelada">
              ✓
            </span>
          )}
        </button>
      );
    }), [opciones, currentQuestion, respuestaActual, respuestaCorrecta, disabled, letras, getOptionClass, onRespuesta, isAnswerRevealed]);

  return (
    <div className={styles.testContent}>
      <section
        className={styles.questionContainer}
        role="region"
        aria-label={`Pregunta ${currentQuestion + 1} de ${totalQuestions}`}
        tabIndex="0"
      >
        <h3 className={styles.questionText} id={`question-${currentQuestion}`}>
          {pregunta}
        </h3>
      </section>

      <div
        className={styles.optionsContainer}
        role="listbox"
        aria-labelledby={`question-${currentQuestion}`}
      >
        {renderedOptions}
      </div>
      
      {/* Navigation buttons - only shown in practice mode */}
      {isPracticeMode && (
        <div className={styles.actionButtons}>
          <div className={styles.actionButtonsInner}>
            <button
              onClick={onPrevious}
              disabled={!canGoBack}
              className={styles.navButton}
              aria-label="Pregunta anterior"
            >
              <span className={styles.navButtonIcon} aria-hidden="true">←</span>
              <span>Anterior</span>
            </button>
            <button
              onClick={onNext}
              disabled={!canGoForward}
              className={styles.navButton}
              aria-label="Siguiente pregunta"
            >
              <span>Siguiente</span>
              <span className={styles.navButtonIcon} aria-hidden="true">→</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

TestQuestion.propTypes = {
  pregunta: PropTypes.string.isRequired,
  opciones: PropTypes.arrayOf(PropTypes.string).isRequired,
  letras: PropTypes.arrayOf(PropTypes.string).isRequired,
  respuestaActual: PropTypes.shape({
    seleccionada: PropTypes.number,
    correcta: PropTypes.bool,
    revelada: PropTypes.bool
  }),
  respuestaCorrecta: PropTypes.number.isRequired,
  onRespuesta: PropTypes.func.isRequired,
  mostrarFeedback: PropTypes.bool,
  disabled: PropTypes.bool,
  onPrevious: PropTypes.func,
  onNext: PropTypes.func,
  currentQuestion: PropTypes.number,
  totalQuestions: PropTypes.number,
  hasNextQuestion: PropTypes.bool,
  isPracticeMode: PropTypes.bool
};

export default React.memo(TestQuestion);